import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, X, GripVertical, EyeOff } from 'lucide-react';
import { Card } from '../components/ui/Card';
import { BannerPreviewCard } from '../components/banners/BannerPreviewCard';
import { BannerCropModal } from '../components/banners/BannerCropModal';
import { useAuth } from '../context/AuthContext';
import {
  getBannerCampaign,
  createBannerCampaign,
  updateBannerCampaign,
  uploadFile,
  getResortLocations,
  type BannerItem,
  type BannerCampaignStatus,
} from '../services/resortAdminApi';
import { validateBannerForm } from '../utils/bannerValidation';

const ALL_RESORTS = '__all__';
const MAX_BANNERS = 3;

interface ResortOption { value: string; label: string; }

const STATUS_BADGE: Record<string, string> = {
  Published: 'bg-green-100 text-green-800',
  Draft: 'bg-amber-100 text-amber-800',
  Expired: 'bg-gray-100 text-gray-600',
};

function emptyBanner(): BannerItem {
  return { image: '', image_url: '', title: '', description: '', link: '', button_text: '', text_theme: 'Dark' };
}

export const BannerCampaignDetails: React.FC = () => {
  const { name } = useParams<{ name: string }>();
  const isNew = !name;
  const navigate = useNavigate();
  const { isReadOnly } = useAuth();

  const [resort, setResort] = useState('');
  const [allResorts, setAllResorts] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [banners, setBanners] = useState<BannerItem[]>([emptyBanner()]);
  const [isPublished, setIsPublished] = useState(false);
  const [status, setStatus] = useState<BannerCampaignStatus>('Draft');

  const [resortOptions, setResortOptions] = useState<ResortOption[]>([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Crop modal state
  const [cropFile, setCropFile] = useState<File | null>(null);
  const [cropTarget, setCropTarget] = useState<number | null>(null);
  const fileInputs = useRef<Record<number, HTMLInputElement | null>>({});

  // Drag-reorder
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  useEffect(() => {
    getResortLocations()
      .then(res => {
        const locs = res?.resort_locations || [];
        setResortOptions(locs.map((r: any) => ({ value: r.value, label: r.label || r.value })));
      })
      .catch(() => setResortOptions([]));
  }, []);

  useEffect(() => {
    if (isNew || !name) return;
    setLoading(true);
    getBannerCampaign(name)
      .then(c => {
        setAllResorts(!!c.all_resorts);
        setResort(c.resort || '');
        setStartDate(c.start_date || '');
        setEndDate(c.end_date || '');
        setIsPublished(!!c.is_published);
        setStatus(c.status);
        setBanners(c.banners?.length ? c.banners.map(b => ({ ...emptyBanner(), ...b })) : [emptyBanner()]);
      })
      .catch(e => setError(e?.message || 'Failed to load campaign'))
      .finally(() => setLoading(false));
  }, [name, isNew]);

  const displayStatus = isNew ? 'Draft' : status;

  const updateBanner = (i: number, patch: Partial<BannerItem>) => {
    setBanners(prev => prev.map((b, idx) => (idx === i ? { ...b, ...patch } : b)));
  };
  const removeBanner = (i: number) => {
    setBanners(prev => (prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev));
  };
  const addBanner = () => {
    setBanners(prev => (prev.length < MAX_BANNERS ? [...prev, emptyBanner()] : prev));
  };

  const onPickFile = (i: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-picking the same file
    if (!file) return;
    setCropFile(file);
    setCropTarget(i);
  };

  const onCropApplied = async (croppedFile: File) => {
    const i = cropTarget;
    setCropFile(null);
    setCropTarget(null);
    if (i == null) return;
    try {
      const previewUrl = URL.createObjectURL(croppedFile);
      updateBanner(i, { image_url: previewUrl }); // instant preview
      const res = await uploadFile(croppedFile);
      updateBanner(i, { image: res.file_url }); // raw path saved to backend
    } catch (e: any) {
      setError(e?.message || 'Failed to upload image');
    }
  };

  const reorder = (from: number, to: number) => {
    if (from === to) return;
    setBanners(prev => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  const buildPayload = (publish: boolean) => ({
    resort: allResorts ? null : (resort || null),
    all_resorts: allResorts,
    start_date: startDate,
    end_date: endDate,
    is_published: publish,
    priority: 0,
    banners: banners.map(b => ({
      image: b.image,
      title: b.title || '',
      description: b.description || '',
      link: b.link || '',
      button_text: b.button_text || '',
      text_theme: b.text_theme,
    })),
  });

  const save = async (publish: boolean) => {
    setError('');
    setSuccess('');

    // Publishing runs the full validation; a draft only needs targeting + dates.
    if (publish) {
      const errs = validateBannerForm({ resort, all_resorts: allResorts, start_date: startDate, end_date: endDate, banners });
      if (errs.length) { setError(errs[0]); return; }
    } else {
      if (!allResorts && !resort) { setError('Select a resort or choose "All resorts"'); return; }
      if (!startDate || !endDate) { setError('Start and end dates are required'); return; }
      if (startDate > endDate) { setError('Start date must be before end date'); return; }
    }

    setSaving(true);
    try {
      const payload = buildPayload(publish);
      if (isNew) {
        const res = await createBannerCampaign(payload);
        const created = res?.campaign?.name;
        if (created) {
          navigate(`/banners/${encodeURIComponent(created)}`, { replace: true });
        } else {
          navigate('/banners');
        }
      } else {
        await updateBannerCampaign({ name: name!, ...payload });
        setIsPublished(publish);
        setSuccess('Campaign saved');
        // Refresh derived status from the server.
        const c = await getBannerCampaign(name!);
        setStatus(c.status);
        setIsPublished(!!c.is_published);
      }
    } catch (e: any) {
      setError(e?.response?.data?._error_message || e?.message || 'Failed to save campaign');
    } finally {
      setSaving(false);
    }
  };

  const resortSelectValue = allResorts ? ALL_RESORTS : resort;
  const onResortChange = (v: string) => {
    if (v === ALL_RESORTS) { setAllResorts(true); setResort(''); }
    else { setAllResorts(false); setResort(v); }
  };

  const previewBanners = useMemo(() => banners.filter(b => b.image || b.image_url || b.title), [banners]);

  if (loading) {
    return <div className="flex h-64 items-center justify-center">Loading…</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <button onClick={() => navigate('/banners')} className="rounded-full p-2 hover:bg-gray-100">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <h1 className="text-2xl font-semibold text-gray-800">Banner</h1>
          <span className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${STATUS_BADGE[displayStatus] || 'bg-amber-100 text-amber-800'}`}>
            {displayStatus}
          </span>
        </div>

        {!isReadOnly && (
          <div className="flex space-x-2">
            {isPublished ? (
              <>
                <button
                  onClick={() => save(false)}
                  disabled={saving}
                  className="inline-flex items-center rounded-md border border-orange-400 bg-orange-50 px-4 py-2 text-sm font-medium text-orange-700 hover:bg-orange-100 disabled:opacity-50"
                >
                  <EyeOff className="mr-2 h-4 w-4" />
                  Hide
                </button>
                <button
                  onClick={() => save(true)}
                  disabled={saving}
                  className="inline-flex items-center rounded-md bg-alps-primary px-4 py-2 text-sm font-medium text-white hover:bg-alps-primary-dark disabled:opacity-50"
                >
                  {saving ? 'Saving…' : 'Publish Changes'}
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => save(false)}
                  disabled={saving}
                  className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Save draft
                </button>
                <button
                  onClick={() => save(true)}
                  disabled={saving}
                  className="inline-flex items-center rounded-md bg-alps-primary px-4 py-2 text-sm font-medium text-white hover:bg-alps-primary-dark disabled:opacity-50"
                >
                  {saving ? 'Saving…' : 'Publish'}
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {error && <div className="border-l-4 border-red-500 bg-red-50 p-4"><p className="text-sm text-red-700">{error}</p></div>}
      {success && <div className="border-l-4 border-green-500 bg-green-50 p-4"><p className="text-sm text-green-700">{success}</p></div>}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Left: form */}
        <Card>
          <div className="space-y-6 p-2">
            {/* Resort + dates */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700">Resort <span className="text-red-500">*</span></label>
                <select
                  value={resortSelectValue}
                  onChange={e => onResortChange(e.target.value)}
                  disabled={isReadOnly}
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-alps-primary focus:outline-none focus:ring-alps-primary disabled:bg-gray-100"
                >
                  <option value="">Select a resort…</option>
                  <option value={ALL_RESORTS}>All resorts</option>
                  {resortOptions.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">Active dates <span className="text-red-500">*</span></label>
                <div className="mt-1 flex items-center gap-2">
                  <input
                    type="date"
                    value={startDate}
                    onChange={e => setStartDate(e.target.value)}
                    disabled={isReadOnly}
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-alps-primary focus:outline-none focus:ring-alps-primary disabled:bg-gray-100"
                  />
                  <input
                    type="date"
                    value={endDate}
                    onChange={e => setEndDate(e.target.value)}
                    disabled={isReadOnly}
                    className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-alps-primary focus:outline-none focus:ring-alps-primary disabled:bg-gray-100"
                  />
                </div>
              </div>
            </div>

            <hr className="border-gray-200" />

            {/* Banner blocks */}
            {banners.map((banner, i) => (
              <div
                key={i}
                className="space-y-4"
                onDragOver={e => { if (dragIndex !== null) e.preventDefault(); }}
                onDrop={() => { if (dragIndex !== null) { reorder(dragIndex, i); setDragIndex(null); } }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {!isReadOnly && (
                      <span
                        draggable
                        onDragStart={() => setDragIndex(i)}
                        onDragEnd={() => setDragIndex(null)}
                        className="cursor-grab text-gray-400 hover:text-gray-600"
                        title="Drag to reorder"
                      >
                        <GripVertical className="h-5 w-5" />
                      </span>
                    )}
                    <h3 className="text-lg font-bold text-gray-900">Banner {i + 1}</h3>
                    {banners.length > 1 && !isReadOnly && (
                      <button onClick={() => removeBanner(i)} className="text-gray-400 hover:text-red-500" title="Remove banner">
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>

                  {/* Dark / Light text toggle */}
                  <div className="inline-flex rounded-md bg-gray-100 p-1">
                    {(['Dark', 'Light'] as const).map(theme => (
                      <button
                        key={theme}
                        onClick={() => updateBanner(i, { text_theme: theme })}
                        disabled={isReadOnly}
                        className={`rounded px-3 py-1 text-sm font-medium transition-colors ${
                          banner.text_theme === theme ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'
                        }`}
                      >
                        {theme} text
                      </button>
                    ))}
                  </div>
                </div>

                {/* Image */}
                <div>
                  <label className="block text-sm font-medium text-gray-700">Image <span className="text-red-500">*</span></label>
                  <p className="text-sm text-gray-400">min. 1200×514px · ratio 21:9</p>
                  <input
                    ref={el => (fileInputs.current[i] = el)}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={e => onPickFile(i, e)}
                  />
                  {banner.image || banner.image_url ? (
                    <div className="mt-2 space-y-2">
                      <div className="w-40 overflow-hidden rounded-md border border-gray-200" style={{ aspectRatio: '21 / 9' }}>
                        <img src={banner.image_url || banner.image} alt="" className="h-full w-full object-cover" />
                      </div>
                      {!isReadOnly && (
                        <div className="flex gap-2">
                          <button onClick={() => fileInputs.current[i]?.click()} className="rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50">
                            Change Image
                          </button>
                          <button onClick={() => updateBanner(i, { image: '', image_url: '' })} className="rounded-md border border-red-200 bg-white px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50">
                            Remove
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <button
                      onClick={() => fileInputs.current[i]?.click()}
                      disabled={isReadOnly}
                      className="mt-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-alps-primary shadow-sm hover:bg-gray-50 disabled:opacity-50"
                    >
                      Upload Image
                    </button>
                  )}
                </div>

                {/* Title / Description */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Title</label>
                    <input
                      type="text"
                      value={banner.title || ''}
                      onChange={e => updateBanner(i, { title: e.target.value })}
                      disabled={isReadOnly}
                      placeholder="Add title"
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-alps-primary focus:outline-none focus:ring-alps-primary disabled:bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Description</label>
                    <input
                      type="text"
                      value={banner.description || ''}
                      onChange={e => updateBanner(i, { description: e.target.value })}
                      disabled={isReadOnly}
                      placeholder="Add description"
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-alps-primary focus:outline-none focus:ring-alps-primary disabled:bg-gray-100"
                    />
                  </div>
                </div>

                {/* Link / Button text (button disabled until link provided) */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Link</label>
                    <input
                      type="text"
                      value={banner.link || ''}
                      onChange={e => {
                        const link = e.target.value;
                        updateBanner(i, link ? { link } : { link, button_text: '' });
                      }}
                      disabled={isReadOnly}
                      placeholder="Add link"
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-alps-primary focus:outline-none focus:ring-alps-primary disabled:bg-gray-100"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Button text</label>
                    <input
                      type="text"
                      value={banner.button_text || ''}
                      onChange={e => updateBanner(i, { button_text: e.target.value })}
                      disabled={isReadOnly || !banner.link}
                      placeholder="Add button text"
                      className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:border-alps-primary focus:outline-none focus:ring-alps-primary disabled:cursor-not-allowed disabled:bg-gray-100"
                    />
                  </div>
                </div>

                {i < banners.length - 1 && <hr className="border-gray-200" />}
              </div>
            ))}

            {banners.length < MAX_BANNERS && !isReadOnly && (
              <button
                onClick={addBanner}
                className="inline-flex items-center rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-alps-primary shadow-sm hover:bg-gray-50"
              >
                <Plus className="mr-2 h-5 w-5" />
                Add banner
              </button>
            )}
          </div>
        </Card>

        {/* Right: live preview */}
        <div>
          <Card>
            <div className="p-2">
              <p className="mb-4 text-sm font-medium text-gray-700">Live preview</p>
              <BannerPreviewCard banners={previewBanners.length ? previewBanners : banners} />
            </div>
          </Card>
        </div>
      </div>

      {cropFile && (
        <BannerCropModal
          file={cropFile}
          onCancel={() => { setCropFile(null); setCropTarget(null); }}
          onApply={onCropApplied}
        />
      )}
    </div>
  );
};
